import { Chain, Timeframe } from '@ankr.com/chains-list';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { JWT } from 'domains/jwtToken/types';
import { hasAllBlockchainsInWhitelist } from 'modules/projects/utils/hasAllBlockchainsInWhitelist';
import {
  projectsRoutesConfig,
  projectsV2RoutesConfig,
} from 'domains/projects/routes';
import {
  selectAllPathsByChainId,
  selectPrivateChainById,
} from 'modules/chains/store/selectors';
import { selectDraftUserEndpointToken } from 'domains/projects/store';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAppSelector } from 'store/useAppSelector';
import { useJWTStatus } from 'domains/jwtToken/hooks/useJWTStatus';
import { useJWTs } from 'domains/jwtToken/hooks/useJWTs';
import { useProjectWhitelistBlockchains } from 'domains/projects/hooks/useProjectWhitelistBlockchains';

import { useProjectRequestsCount } from './useProjectRequestsCount';

export interface IUseChainProjectItemProps {
  chain: Chain;
  jwt: JWT;
  timeframe: Timeframe;
}

export const useChainProjectItem = ({
  chain: nestedChain,
  jwt,
  timeframe,
}: IUseChainProjectItemProps) => {
  const { accountsLoading, group, isAccountInitialized } = useAccount();
  const draftUserEndpointToken = useAppSelector(state =>
    selectDraftUserEndpointToken(state, { group }),
  );

  const { userEndpointToken } = jwt;
  const isDraft = userEndpointToken === draftUserEndpointToken;

  const chain =
    useAppSelector(state =>
      selectPrivateChainById(state, nestedChain.id, userEndpointToken),
    ) || nestedChain;

  const allChainPaths = useAppSelector(state =>
    selectAllPathsByChainId(state, chain.id),
  );

  const { requestsCount, requestsCountLoading } = useProjectRequestsCount({
    token: userEndpointToken,
    timeframe,
  });

  const {
    state: { isSuccess: projectsLoaded },
  } = useJWTs({ group, skipFetching: true });

  const skipFetching =
    accountsLoading || !isAccountInitialized || !projectsLoaded;

  const {
    loading: projectWhitelistBlockchainsLoading,
    projectWhitelistBlockchains,
  } = useProjectWhitelistBlockchains({
    group,
    skipFetching,
    token: userEndpointToken,
  });

  const { jwtStatus: projectStatus, loading: projectStatusLoading } =
    useJWTStatus({
      group,
      skipFetching,
      userEndpointToken,
    });

  const isCurrentPathIncluded = useMemo(() => {
    const hasProjectBlockchains = projectWhitelistBlockchains.length > 0;
    const isChainPathIncludedInProjectBlockchains = allChainPaths.some(path =>
      projectWhitelistBlockchains.includes(path),
    );

    return hasProjectBlockchains && isChainPathIncludedInProjectBlockchains;
  }, [allChainPaths, projectWhitelistBlockchains]);

  const isCurrentChainIncluded =
    isCurrentPathIncluded ||
    hasAllBlockchainsInWhitelist(projectWhitelistBlockchains);

  const navigate = useNavigate();

  const projectPath = useMemo(
    () =>
      isDraft
        ? projectsV2RoutesConfig.editProjectChains.generatePath(jwt.index)
        : projectsRoutesConfig.project.generatePath(jwt.index),
    [isDraft, jwt.index],
  );

  const handleGoToProject = useCallback(() => {
    navigate(projectPath);
  }, [navigate, projectPath]);

  return {
    chain,
    handleGoToProject,
    isCurrentChainIncluded,
    isDraft,
    projectPath,
    projectStatus,
    projectStatusLoading,
    projectWhitelistBlockchains,
    projectWhitelistBlockchainsLoading,
    requestsCount,
    requestsCountLoading,
  };
};
